import { WorkerTransferFlow } from "@/components/roster/WorkerTransferFlow";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/design-system";
import { CrewEditor } from "@/components/workflow/CrewPanel";
import { cardClass } from "@/components/workflow/shared";
import { useAuth } from "@/context/AuthContext";
import { datum } from "@/lib/format";
import type { Raspored } from "@/lib/gradilista";
import { memberOn, unassignedWorkers } from "@/lib/siteCrewTasks";
import { type Crew, type SiteTask } from "@/lib/workflow";

export function CrewBoard({
  siteId,
  date,
  crews,
  tasks,
  roster,
  refresh,
  readRoster,
  transitional = false,
}: {
  siteId: string;
  date: string;
  crews: Crew[];
  tasks: SiteTask[];
  roster: Raspored[];
  refresh: () => void;
  readRoster: boolean;
  transitional?: boolean;
}) {
  const { can } = useAuth();
  const [editor, setEditor] = useState<Crew | "new" | null>(null);
  const active = crews.filter((c) => c.aktivna);
  const names = new Map(
    roster.map((r) => [r.radnik_id, r.radnik ?? "Nepoznati radnik"]),
  );
  const unassigned = unassignedWorkers(roster, crews, date, transitional);
  const effectiveWorkers = new Set(
    roster
      .filter(
        (r) => memberOn(r, date) && (transitional || !r.ceka_blokada_override),
      )
      .map((r) => r.radnik_id),
  );
  function person(id: string, leader = false) {
    return (
      <div key={id} className="flex min-h-11 items-center gap-3 border-t py-2">
        <span>
          {names.get(id) ?? "Radnik više nije na popisu"}
          {leader && <strong className="block text-sm">Brigadir</strong>}
        </span>
        {readRoster && effectiveWorkers.has(id) && (
          <WorkerTransferFlow
            radnikId={id}
            trenutniRaspored={
              roster.find(
                (r) =>
                  r.radnik_id === id &&
                  memberOn(r, date) &&
                  (transitional || !r.ceka_blokada_override),
              ) ?? null
            }
            zadanoGradilisteId={siteId}
            onDone={refresh}
          />
        )}
      </div>
    );
  }
  return (
    <section className="space-y-4" aria-label="Brigade gradilišta">
      <div className="flex flex-wrap gap-3">
        {can("brigada", "create") && readRoster && (
          <Button className="min-h-11" onClick={() => setEditor("new")}>
            Nova brigada
          </Button>
        )}
      </div>
      {!readRoster && (
        <p>
          Za prikaz imena i ljudi bez brigade potreban je pristup popisu ljudi.
        </p>
      )}
      <div className="grid items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
        {readRoster && (
          <article className={`${cardClass} border-dashed border-amber-600`}>
            <h2 className="text-lg font-bold">
              Bez brigade ({unassigned.length})
            </h2>
            <p className="text-sm">
              Ljudi na gradilištu koji na odabrani dan nisu u brigadi.
            </p>
            {unassigned.map((r) => person(r.radnik_id))}
          </article>
        )}
        {active.map((crew) => {
          const members = crew.clanovi.filter((m) => memberOn(m, date));
          const leader = members.find((m) => m.je_brigadir);
          const current = tasks.filter(
            (t) =>
              t.brigada_id === crew.id &&
              t.status === "aktivan" &&
              t.planirani_od <= date &&
              date <= t.planirani_do,
          );
          return (
            <article key={crew.id} className={cardClass}>
              <h2 className="text-lg font-bold">
                {crew.naziv} ({members.length})
              </h2>
              {leader ? (
                <p>
                  Brigadir:{" "}
                  {names.get(leader.radnik_id) ?? "Radnik više nije na popisu"}
                </p>
              ) : (
                <p className="rounded bg-red-100 p-2 font-bold text-red-900">
                  Bez brigadira
                </p>
              )}
              <p className="text-sm">
                Trenutni takt:{" "}
                {current.map((t) => t.naziv).join(", ") ||
                  "Nema aktivnog takta"}
              </p>
              <div>
                {members.map((m) => person(m.radnik_id, m.je_brigadir))}
              </div>
              <details>
                <summary className="flex min-h-11 cursor-pointer items-center font-semibold">
                  Povijest ekipe
                </summary>
                <ul className="space-y-2">
                  {crew.clanovi.map((m, i) => (
                    <li key={m.id ?? i}>
                      {names.get(m.radnik_id) ?? "Radnik više nije na popisu"}
                      {m.je_brigadir && " · brigadir"}
                      <span className="block text-sm">
                        {datum(m.vrijedi_od)} –{" "}
                        {m.vrijedi_do ? datum(m.vrijedi_do) : "bez kraja"}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
              {can("brigada", "update") && readRoster && (
                <Button
                  className="min-h-11"
                  variant="outline"
                  onClick={() => setEditor(crew)}
                >
                  Uredi brigadu
                </Button>
              )}
            </article>
          );
        })}
      </div>
      {editor && (
        <Dialog
          title={editor === "new" ? "Nova brigada" : "Uredi brigadu"}
          onClose={() => setEditor(null)}
        >
          <CrewEditor
            siteId={siteId}
            crew={editor === "new" ? null : editor}
            roster={roster}
            onClose={() => setEditor(null)}
            onSaved={() => {
              setEditor(null);
              refresh();
            }}
          />
        </Dialog>
      )}
    </section>
  );
}
